// SEND-142 (Howard ruled 2026-08-27) — THE RAIL SPLIT. The editor's right
// column was one 300-line block; it is now three panels in three files.
// SAME DATA, SAME BUTTONS, SAME confirm / refuse / pull-in. No new field,
// no new formula, no new call: every figure still arrives from the server's
// own quantities and every decision still lives in the editor.
import React from "react";

import { CATEGORIES, SIDING } from "./marks";

// SEND-155 (Howard ruled 2026-08-31) — THE COPY-OUT LIST HE TYPES AN ORDER
// FROM. The SERVER writes every row and writes the plain text itself; this
// component prints that text and puts the server's own string on the
// clipboard. It composes no size, picks no size, and knows no price.
// SEND-302e (Howard ruled 2026-06) — HIDE THE BLUE NOTE IN THE OPENINGS
// LIST. "It says the list is the same across every photo, openings are not
// deducted from siding, and the marks are guidance-confirmed. Hide it. The
// opening rows stay." Three sentences, two places, two flags — and HIDDEN,
// NOT DELETED: the server still writes `opening_order_note`, `openings_note`
// and `guidance_confirmed_note`, the markup and testids stay on disk, and the
// order rows, the scope buttons, Copy and the Print / PDF link are untouched.
// EVERY OTHER REFUSAL ON THIS PANEL STAYS — a refusal is evidence, not
// chrome: the provisional note, the openings-without-extent note, the gable
// refusals and pitch warnings, the dormer refusals and the product basis
// notes all still print.
const ORDER_LIST_NOTE_SHOWN = false;
const OPENINGS_AND_GUIDANCE_NOTES_SHOWN = false;
// SEND-302f (Howard ruled 2026-06) — HIDE THE READ-OFF LINE UNDER EACH
// OPENING. "Each opening still has a blue line under the size: READ OFF THIS
// PHOTO · WIDTH × HEIGHT. Hide that line. The size stays, such as 31 x 53."
// All three wordings go with it — READ OFF THIS PHOTO, TYPED · THE ORDER and
// the AI's-stated-size line — together with their axis and ft² tail and the
// SEND-154 read sentence under a typed opening. HIDDEN, NOT DELETED: the
// markup, the wordings, the testid `photo-takeoff-opening-row-read-*` and the
// server's `typed_by` / `axis` / `sqft` / `read_line` fields all stay. WHAT
// STAYS ON SCREEN: the size headline, and the REFUSAL when there is no size —
// a refusal is evidence, not chrome.
const OPENING_ROW_BASIS_SHOWN = false;

const OrderList = ({ qty, orderAll, estId }) => {
  const [copied, setCopied] = React.useState(false);
  const [scope, setScope] = React.useState("all");
  const all = scope === "all" && orderAll;
  const text = all ? orderAll.text : qty?.opening_order_text;
  const note = all ? orderAll.note : qty?.opening_order_note;
  const copy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="mt-2 border border-[var(--border)] p-1.5" data-testid="photo-takeoff-order-list">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--ink-2)]">
          Order list — confirmed openings
        </div>
        {text && (
          <button type="button" onClick={copy}
            className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-[var(--ink)] hover:bg-[var(--ink)] hover:text-white transition-colors"
            data-testid="photo-takeoff-order-copy-btn">
            {copied ? "Copied" : "Copy"}
          </button>
        )}
      </div>
      {/* SEND-156 — the one-page sheet for the supply counter: typed order
          sizes only, one row each, printed or saved as PDF. */}
      {estId && (
        <a href={`/estimate/${estId}/opening-list`} target="_blank" rel="noreferrer"
          className="mt-1 inline-block text-[9px] font-bold uppercase tracking-wider underline text-[var(--ink-2)] hover:text-[var(--ink)]"
          data-testid="photo-takeoff-order-print-link">
          Print / PDF the opening list
        </a>
      )}
      {orderAll && (
        <div className="mt-1 flex gap-1">
          <button type="button" onClick={() => setScope("all")}
            className={`px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider border ${scope === "all" ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--border)] text-[var(--ink-2)]"}`}
            data-testid="photo-takeoff-order-scope-all">
            All photos{orderAll.photo_count ? ` (${orderAll.photo_count})` : ""}
          </button>
          <button type="button" onClick={() => setScope("photo")}
            className={`px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider border ${scope === "photo" ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--border)] text-[var(--ink-2)]"}`}
            data-testid="photo-takeoff-order-scope-photo">
            This photo
          </button>
        </div>
      )}
      {text && (
        <pre className="mt-1 text-[10px] leading-snug whitespace-pre-wrap font-mono text-[var(--ink)]"
          data-testid="photo-takeoff-order-text">{text}</pre>
      )}
      {ORDER_LIST_NOTE_SHOWN ? (
      <div className="mt-0.5 text-[9px] text-[var(--ink-2)] leading-snug" data-testid="photo-takeoff-order-note">
        {note}
      </div>
      ) : null}
    </div>
  );
};

// SEND-302c (Howard ruled 2026-06) — TWO HIDES ON THIS PANEL, THE SAME
// ONE-FLAG PATTERN THE SCREEN ALREADY USES.
//   1. the blue paragraph under the opening sizes explaining width × height.
//      THE OPENING ROWS STAY, and so does their header line.
//   2. the yellow PLANE: UNKNOWN note. THE LIVE QUANTITY LINES STAY.
// NOTHING IS DELETED: both blocks, their testids, their exact wording and
// the server fields behind them (`opening_sizes_note`, `plane_basis`,
// `plane_basis_reason`) stay on disk and are still tested. No quantity, no
// size and no plane math was touched. Flip either flag to show it again.
const OPENING_SIZES_NOTE_SHOWN = false;
const PLANE_BASIS_BOX_SHOWN = false;

export const QuantitiesPanel = ({ qty, orderAll, estId }) => (
  <>
            {/* QUANTITIES */}
            <div className="p-3 border-b border-[var(--border)]">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--muted)] mb-1">Live quantities — confirmed marks only</div>
              {/* NAME THE PLANE (SEND-136). Printed wherever the quantity
                  appears. UNKNOWN is the honest starting state — never a
                  default that reads as square-on. */}
              {PLANE_BASIS_BOX_SHOWN ? (
              <div className="mb-1.5 p-1.5 border text-[10px] leading-snug"
                style={{
                  borderColor: qty?.plane_basis === "SQUARE-ON" ? "var(--success)" : qty?.plane_basis === "OBLIQUE" ? "#DC2626" : "#F59E0B",
                  background: qty?.plane_basis === "SQUARE-ON" ? "#ECFDF5" : qty?.plane_basis === "OBLIQUE" ? "#FEF2F2" : "#FEF3C7",
                }}
                data-testid="photo-takeoff-plane-basis">
                <b className="uppercase tracking-wider">Plane: {qty?.plane_basis || "UNKNOWN"}</b>
                <div className="mt-0.5 text-[var(--ink-2)]">{qty?.plane_basis_reason || "reading this photo's marks…"}</div>
              </div>
              ) : null}
              <div className="grid grid-cols-2 gap-1 text-[11px]">
                <div>Siding</div><div className="font-bold text-right" data-testid="photo-takeoff-qty-siding">{qty?.siding_sqft ?? "—"} {qty?.siding_sqft != null ? "ft²" : ""}</div>
                <div>Non-siding</div><div className="font-bold text-right" data-testid="photo-takeoff-qty-nonsiding">{qty?.non_siding_sqft ?? "—"} {qty?.non_siding_sqft != null ? "ft²" : ""}</div>
                <div>Openings</div><div className="font-bold text-right" data-testid="photo-takeoff-qty-opening-count">{qty?.opening_count ?? "—"}</div>
                <div>Opening ft²</div><div className="font-bold text-right" data-testid="photo-takeoff-qty-opening-sqft">{qty?.opening_sqft ?? "—"}</div>
                {/* SEND-153 — the ft² is a SECOND line for the siding
                    deduction. The headline lives in the opening list below,
                    in inches, width first. */}
                {/* SEND-139 — the gable and dormer lanes. A lane with
                    nothing measured shows an em dash, never a 0. */}
                <div>Gable ft²</div><div className="font-bold text-right" data-testid="photo-takeoff-qty-gable">{qty?.gable_sqft ?? "—"} {qty?.gable_sqft != null ? "ft²" : ""}</div>
                <div>Dormer face ft²</div><div className="font-bold text-right" data-testid="photo-takeoff-qty-dormer-face">{qty?.dormer_face_sqft ?? "—"} {qty?.dormer_face_sqft != null ? "ft²" : ""}</div>
                <div>Dormer cheeks ft²</div><div className="font-bold text-right" data-testid="photo-takeoff-qty-dormer-cheeks">{qty?.dormer_cheek_sqft ?? "—"} {qty?.dormer_cheek_sqft != null ? "ft²" : ""}</div>
              </div>
              {/* SEND-153 — THE LIVE OPENING LIST. The headline per opening is
                  its OUTSIDE DIMENSION in inches, width first; the ft² sits
                  under it for the siding deduction only. A typed size wins and
                  says so; no scale means the sentence, never a number. */}
              {qty?.opening_rows && (
                <div className="mt-1.5" data-testid="photo-takeoff-opening-sizes">
                  <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--ink-2)]">
                    Openings — outside dimensions (width × height)
                  </div>
                  {qty.opening_rows.map((r, i) => (
                    <div key={r.id || i} className="mt-0.5 flex items-start justify-between gap-2"
                      data-testid={`photo-takeoff-opening-row-${i}`}>
                      <span className="text-[10px] text-[var(--ink-2)] uppercase tracking-wider">{r.label}</span>
                      <span className="text-right">
                        <span className="text-[12px] font-bold text-[var(--ink)]" data-testid={`photo-takeoff-opening-row-size-${i}`}>
                          {r.size || "—"}
                        </span>
                        {OPENING_ROW_BASIS_SHOWN && r.size && (
                          <span className="block text-[8px] uppercase tracking-wider text-[var(--ink-2)]">
                            {r.typed_by === "you" ? "typed · the order"
                              : r.typed_by === "the AI read" ? "AI's stated size · confirm or type yours"
                                : "read off this photo"}
                            {` · ${r.axis}`}
                            {r.sqft != null ? ` · ${r.sqft} ft²` : ""}
                          </span>
                        )}
                        {/* SEND-154 — the photo-derived figure, with its axis
                            named, in the server's own words. */}
                        {OPENING_ROW_BASIS_SHOWN && r.typed && r.read_line && (
                          <span className="block text-[8px] text-[var(--ink-2)] leading-snug"
                            data-testid={`photo-takeoff-opening-row-read-${i}`}>
                            {r.read_line}
                          </span>
                        )}
                        {!r.size && r.refusal && (
                          <span className="block text-[9px] font-bold text-[var(--warning-text)] leading-snug max-w-[13rem]"
                            data-testid={`photo-takeoff-opening-row-refusal-${i}`}>
                            {r.refusal}
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                  {OPENING_SIZES_NOTE_SHOWN ? (
                  <div className="mt-0.5 text-[9px] text-[var(--ink-2)] leading-snug" data-testid="photo-takeoff-opening-sizes-note">
                    {qty.opening_sizes_note}
                  </div>
                  ) : null}
                </div>
              )}
              <OrderList qty={qty} orderAll={orderAll} estId={estId} />
              {qty?.gable_basis_note && (
                <div className="mt-1.5 text-[9px] text-[var(--success)] font-bold leading-snug" data-testid="photo-takeoff-gable-basis">
                  {qty.gable_basis_note}
                </div>
              )}
              {qty?.non_siding_by_category && (
                <div className="mt-1.5 flex flex-wrap gap-1" data-testid="photo-takeoff-qty-by-category">
                  {Object.entries(qty.non_siding_by_category).map(([k, v]) => (
                    <span key={k} className="text-[9px] font-bold px-1.5 py-0.5" style={{ background: `${CATEGORIES.find((c) => c.key === k)?.color || "#DC2626"}22`, color: CATEGORIES.find((c) => c.key === k)?.color || "#DC2626" }}>{k} {v} ft²</span>
                  ))}
                </div>
              )}
              {qty?.siding_by_product && (
                <div className="mt-1.5 flex flex-wrap gap-1" data-testid="photo-takeoff-qty-by-product">
                  {Object.entries(qty.siding_by_product).map(([k, v]) => (
                    <span key={k} className="text-[9px] font-bold px-1.5 py-0.5" style={{ background: `${SIDING}22`, color: SIDING }}>{k} · {v} ft²</span>
                  ))}
                  {qty.siding_no_product_sqft != null && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 bg-[#FEF3C7] text-[var(--warning-text)]">no product assigned · {qty.siding_no_product_sqft} ft²</span>
                  )}
                </div>
              )}
              {[qty?.provisional_note,
                ...(OPENINGS_AND_GUIDANCE_NOTES_SHOWN
                  ? [qty?.openings_note, qty?.guidance_confirmed_note] : []),
                qty?.openings_without_extent_note,
                ...(qty?.gable_refusals || []),
                ...(qty?.gable_pitch_warnings || []), ...(qty?.dormer_refusals || []),
                ...(qty?.product_basis_notes || [])].filter(Boolean).map((n, i) => (
                <div key={i} className="mt-1.5 text-[10px] text-[var(--warning-text)] leading-snug" data-testid={`photo-takeoff-refusal-${i}`}>· {n}</div>
              ))}
            </div>
  </>
);

export default QuantitiesPanel;
